<template>
  <!-- Rendered only once the settings it summarises have actually arrived. -->
  <ul v-if="items.length > 0" class="trust-strip">
    <li v-for="item in items" :key="item.key" class="trust-item">
      <span class="trust-icon" aria-hidden="true" v-html="item.icon"></span>
      <span class="trust-text">{{ item.label }}</span>
    </li>
  </ul>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useBookingSettings } from '@/composables/useBookingSettings'
import { useStripePayment } from '@/composables/useStripePayment'

const ICON_CLOCK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>'
const ICON_SHIELD = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6z"/><polyline points="9 12 11 14 15 10"/></svg>'
const ICON_CARD = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="6" width="18" height="12" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/></svg>'
// Storefront, not a card: when no online payment is taken we must not imply
// which methods the studio accepts at the counter. See the note on trust.* in i18n.js.
const ICON_VENUE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 10l1.6-5h14.8L21 10"/><path d="M5 10v9h14v-9"/><path d="M10 19v-5h4v5"/></svg>'

export default {
  name: 'TrustStrip',
  setup() {
    const { t } = useI18n()
    const { settings: bookingSettings, isLoaded: bookingLoaded, load: loadBookingSettings } =
      useBookingSettings()
    const { stripePublicConfig, fetchStripePublicConfig } = useStripePayment()
    const stripeLoaded = ref(false)

    const items = computed(() => {
      const list = []

      if (bookingLoaded.value) {
        if (bookingSettings.value.allowSameDayBooking) {
          list.push({ key: 'sameDay', icon: ICON_CLOCK, label: t('trust.sameDay') })
        }
        const hours = bookingSettings.value.cancellationHours
        if (hours) {
          list.push({ key: 'cancelFree', icon: ICON_SHIELD, label: t('trust.cancelFree', { hours }) })
        }
      }

      if (stripeLoaded.value) {
        // onlinePaymentEnabled only tells us whether Stripe can take a card at
        // booking time. Nothing in the system records which methods a studio
        // accepts on site, so neither branch may name or imply one.
        const online = stripePublicConfig.value.onlinePaymentEnabled
        list.push({
          key: 'payment',
          icon: online ? ICON_CARD : ICON_VENUE,
          label: online ? t('trust.payOnline') : t('trust.payAtVenue')
        })
      }

      return list
    })

    onMounted(() => {
      loadBookingSettings()
      fetchStripePublicConfig().then(() => {
        stripeLoaded.value = true
      })
    })

    return { items }
  }
}
</script>

<style scoped>
.trust-strip {
  width: min(100% - 2 * var(--gutter), 1200px);
  margin: 0 auto;
  padding: var(--s-4) 0 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--s-3) var(--s-5);
}

.trust-item {
  display: inline-flex;
  align-items: center;
  gap: var(--s-2);
  color: var(--text-secondary);
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.3;
}

.trust-icon {
  display: inline-flex;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  /* --color-primary is the tenant's foreground brand colour; --color-accent can
     legitimately be the page background on dark themes. */
  color: var(--color-primary);
}

.trust-icon :deep(svg) {
  width: 100%;
  height: 100%;
}
</style>
